import React from "react";
import { useParams } from "react-router-dom";
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Container,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Alert,
  AlertTitle,
} from "@mui/material";
import {
  TrendingUp,
  Assessment,
  LocationOn,
  Warning,
  Flag,
} from "@mui/icons-material";
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import countries from "../data/countries";

const AgencyStats = () => {
  const { agency } = useParams();
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const agencyData = {
    hrc: {
      name: "الهيئة العليا لإلغاثة (Higher Relief Authority)",
      nameEn: "Higher Relief Authority",
      totalReceived: 2552458,
      totalDistributed: 2114510,
      distributionRate: 82.84,
      undistributed: 437948,
      nationalShare: 90,
      peakMonth: "October 2024",
      peakUnits: "1.9 million units",
      peakWeight: "3,583 tons",
      unitTypes: [
        { type: t("Pieces"), quantity: 1558363, weight: 2332.97 },
        { type: t("Kilograms"), quantity: 691781, weight: 691.76 },
        { type: t("Medicine Boxes (EA)"), quantity: 101200, weight: 2.74 },
        { type: t("Rations"), quantity: 73621, weight: 298.56 },
        { type: t("Boxes"), quantity: 55382, weight: 239.26 },
        { type: t("Bags"), quantity: 29656, weight: 741.24 },
        { type: t("Packages"), quantity: 18518, weight: 1.14 },
        { type: t("Pills"), quantity: 17000, weight: 0 },
        { type: t("Sets"), quantity: 4920, weight: 8.6 },
        { type: t("Cans"), quantity: 2016, weight: 8 },
        { type: t("Containers"), quantity: 1, weight: 1 },
      ],
      topDonors: [
        { country: t("Saudi Arabia"), units: 891133, rate: "80%", flag: countries["KSA"]?.flag },
        { country: t("UAE"), units: 861489, rate: "50%", flag: countries["UAE"]?.flag },
        { country: t("Turkey"), units: 251944, rate: "306%*", flag: countries["Turkey"]?.flag, anomaly: true },
        { country: t("Kuwait"), units: 82302, rate: "62%", flag: countries["Kuwait"]?.flag },
      ],
      categories: [
        { category: t("Medical/Pharmaceutical"), units: 803180 },
        { category: t("Food"), units: 726306 },
        { category: t("Shelter"), units: 564809 },
        { category: t("Health/Hygiene"), units: 304766 },
      ],
      beneficiaries: [
        { type: t("Municipalities"), units: 1812989 },
        { type: t("Displaced Families"), units: 120118 },
        { type: t("Non-governmental Organizations"), units: 36218 },
        { type: t("Governorates"), units: t("Small amounts") },
        { type: t("Religious Institutions"), units: t("Small amounts") },
        { type: t("Official Schools"), units: t("Small amounts") },
        { type: t("Security Forces"), units: t("Small amounts") },
      ],
      geographicIssue: `${t("Unknown Locations")}: 1,501,628 ${t("units")} (71% ${t("of all aid received by Lebanon")})`
    },
    gec: {
      name: "لجنة الطوارئ الحكومية (Government Emergency Committee)",
      nameEn: "Government Emergency Committee",
      totalReceived: 231694,
      totalDistributed: 229970,
      distributionRate: 99.25,
      undistributed: 1724,
      unitTypes: [
        { type: t("Pieces"), quantity: 85507 },
        { type: t("Boxes"), quantity: 74969 },
        { type: t("Rations"), quantity: 37552 },
        { type: t("Bags"), quantity: 25106 },
        { type: t("Kilograms"), quantity: 8515 },
        { type: t("Wooden Boards"), quantity: 45 },
      ],
      focusAreas: [
        { area: t("Food Aid"), percentage: 45 },
        { area: t("Shelter Materials"), percentage: 30 },
        { area: t("Other Categories"), percentage: 25 },
      ],
      topDonors: [
        { country: t("UAE"), received: 126333, distributed: 126152, flag: countries["UAE"]?.flag },
        { country: t("Turkey"), received: 45353, distributed: 45348, flag: countries["Turkey"]?.flag },
        { country: t("Qatar"), received: 19251, distributed: 18051, flag: countries["Qatar"]?.flag },
        { country: t("Kuwait"), received: 11514, distributed: t("Various"), flag: countries["Kuwait"]?.flag },
      ],
      beneficiaries: [
        { type: t("Governorates"), units: 126151 },
        { type: t("Security/Military Institutions"), units: 52153 },
        { type: t("Non-governmental Organizations"), units: 8350 },
        { type: t("Lebanese Red Cross"), units: t("Small amounts") },
        { type: t("Religious Institutions"), units: t("Small amounts") },
        { type: t("Packaging/Processing"), units: t("Some units") },
      ],
      coordination: t("Primary Function: Direct distribution to governorates and security institutions")
    },
    south: {
      name: "مجلس الجنوب (South Lebanon Council)",
      nameEn: "South Lebanon Council",
      totalReceived: 22592,
      totalDistributed: 22249,
      distributionRate: 98.48,
      undistributed: 343,
      nationalShare: "Less than 1%",
      peakMonth: "November 2024",
      totalWeight: "97 tons",
      categories: [t("Heavy Winter Clothing"), t("Blankets"), t("Basic Necessities")],
      topDonors: [
        { country: t("Turkey"), units: 22173, weight: 84.12, flag: countries["Turkey"]?.flag },
        { country: t("Canada"), units: 185, weight: 37, flag: countries["Canada"]?.flag },
        { country: t("China"), units: 97, weight: 10.2, flag: countries["China"]?.flag },
        { country: t("UK"), units: 17, weight: 0.23, flag: countries["UK"]?.flag },
        { country: t("Spain"), units: 120, weight: 25, flag: countries["Spain"]?.flag },
      ],
      villageDistribution: [
        { item: t("Villages Served"), quantity: 257 },
        { item: t("Food Rations Distributed"), quantity: 65868 },
        { item: t("Mattresses"), quantity: 15665 },
        { item: t("Blankets/Covers"), quantity: 21165 },
        { item: t("Pillows"), quantity: 9400 },
        { item: t("Milk Containers"), quantity: 8128 },
        { item: t("Baby Diapers"), quantity: 6570 },
      ],
      coverage: t("Focus Area: South Lebanon villages with direct village-to-village delivery")
    },
    moph: {
      name: "وزارة الصحة العامة (Ministry of Public Health)",
      nameEn: "Ministry of Public Health",
      totalReceived: 667164,
      totalDistributed: null, // Not applicable for MOPH
      distributionRate: null, // Not applicable for MOPH
      undistributed: null, // Not applicable for MOPH
      source: t("from Higher Relief Authority"),
      primaryFunction: t("Medical and pharmaceutical aid distribution"),
      serviceAreas: [t("Chronic diseases"), t("Hospital services"), t("Primary health centers")],
      chronicDiseases: {
        peakMonth: "October 2024",
        totalDistributed: "~950,000 medication doses",
        supportSources: ["WHO", "European Union"],
        topRegions: ["Mount Lebanon", "North Lebanon"],
        affectedRegions: ["Nabatieh", "Baalbek-Hermel (reduced due to displacement)"]
      },
      primaryHealthCenters: {
        totalDistributed: 494563,
        peakPeriod: "November 2024",
        topRegion: "Mount Lebanon",
        secondaryRegions: ["Baalbek-Hermel", "Akkar"],
        lowerRecipients: ["Nabatieh", "North Lebanon"]
      },
      internationalSupport: [
        { organization: t("World Health Organization"), type: t("Medical supplies") },
        { organization: t("UNICEF"), type: t("Child health materials") },
        { organization: t("UN Population Fund"), type: t("Reproductive health supplies") },
        { organization: t("Saudi Arabia"), type: t("Pharmaceutical donations") },
        { organization: t("Qatar"), type: t("Medical equipment") },
        { organization: t("European Union"), type: t("Chronic disease medications") },
      ],
      specializedEquipment: [
        { item: t("Diesel Generators"), quantity: t("20,000+ liters for hospitals") },
        { item: t("Oxygen Equipment"), quantity: t("Various quantities") },
        { item: t("Surgical Supplies"), quantity: t("Large quantities distributed") },
        { item: t("Anesthesia Equipment"), quantity: t("Hospital distribution") },
      ],
      financialPrograms: [
        { program: "Aman Program", beneficiaries: "160,000+ Lebanese families", amount: "$19M+ monthly" },
        { program: "Disability Cash Assistance", beneficiaries: "12,600 individuals", amount: "$540-1M monthly range" },
        { program: "Emergency Assistance", peak: "$5.9M in December" },
      ],
      monthlyFinancial: [
        { month: t("October"), amount: 21.5 },
        { month: t("November"), amount: 24.9 },
        { month: t("December"), amount: 32.5 },
      ]
    }
  };

  const data = agencyData[agency];

  if (!data) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" color="error">
          {t("Agency not found")}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography 
          variant="h2" 
          sx={{ 
            fontWeight: 700,
            mb: 2,
            background: 'linear-gradient(135deg, #0c9fc1 0%, #4db6d1 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {currentLanguage === 'ar' ? data.name : data.nameEn} - {t("Statistical Overview")}
        </Typography>
      </Box>

      {/* Overall Performance */}
      <Paper elevation={2} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
          {t("Overall Performance")}
        </Typography>
        
        <Grid container spacing={4} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                textAlign: 'center', 
                p: 3, 
                borderRadius: 4,
                background: 'linear-gradient(135deg, #0c9fc1 0%, #4db6d1 100%)',
                color: 'white',
                boxShadow: '0 8px 32px rgba(12, 159, 193, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 16px 48px rgba(12, 159, 193, 0.4)',
                }
              }}
            >
              <Box sx={{ mb: 2 }}>
                <TrendingUp sx={{ fontSize: 48, opacity: 0.9 }} />
              </Box>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>{t("Total Aid Received")}</Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                {data.totalReceived ? data.totalReceived.toLocaleString() : 'N/A'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, color: 'white' }}>{t("units")}</Typography>
            </Card>
          </Grid>
          
          {data.totalDistributed !== undefined && data.totalDistributed !== null && (
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                sx={{ 
                  textAlign: 'center', 
                  p: 3, 
                  borderRadius: 4,
                  background: 'linear-gradient(135deg, #38a169 0%, #68d391 100%)',
                  color: 'white',
                  boxShadow: '0 8px 32px rgba(56, 161, 105, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 16px 48px rgba(56, 161, 105, 0.4)',
                  }
                }}
              >
                <Box sx={{ mb: 2 }}>
                  <Assessment sx={{ fontSize: 48, opacity: 0.9 }} />
                </Box>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>{t("Total Distributed")}</Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                  {data.totalDistributed.toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, color: 'white' }}>{t("units")}</Typography>
              </Card>
            </Grid>
          )}
          
          {data.distributionRate !== undefined && data.distributionRate !== null && (
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                sx={{ 
                  textAlign: 'center', 
                  p: 3, 
                  borderRadius: 4,
                  background: 'linear-gradient(135deg, #3182ce 0%, #63b3ed 100%)',
                  color: 'white',
                  boxShadow: '0 8px 32px rgba(49, 130, 206, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 16px 48px rgba(49, 130, 206, 0.4)',
                  }
                }}
              >
                <Box sx={{ mb: 2 }}>
                  <TrendingUp sx={{ fontSize: 48, opacity: 0.9 }} />
                </Box>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>{t("Distribution Rate")}</Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                  {data.distributionRate}%
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, color: 'white' }}>{t("efficiency")}</Typography>
              </Card>
            </Grid>
          )}
          
          {data.undistributed !== undefined && data.undistributed !== null && (
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                sx={{ 
                  textAlign: 'center', 
                  p: 3, 
                  borderRadius: 4,
                  background: 'linear-gradient(135deg, #d69e2e 0%, #f6e05e 100%)',
                  color: 'white',
                  boxShadow: '0 8px 32px rgba(214, 158, 46, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 16px 48px rgba(214, 158, 46, 0.4)',
                  }
                }}
              >
                <Box sx={{ mb: 2 }}>
                  <Warning sx={{ fontSize: 48, opacity: 0.9 }} />
                </Box>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>{t("Undistributed")}</Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                  {data.undistributed.toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, color: 'white' }}>{t("units")}</Typography>
              </Card>
            </Grid>
          )}
        </Grid>

        {data.nationalShare && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {t("Share of Total National Aid")}: {data.nationalShare}% {t("of all aid received by Lebanon")}
            </Typography>
          </Alert>
        )}

        {data.peakMonth && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {t("Peak Activity Period")}
            </Typography>
            <Typography variant="body1">
              <strong>{t("Highest Receipt Month")}:</strong> {data.peakMonth} ({data.peakUnits}{data.peakWeight && `, ${data.peakWeight}`})
            </Typography>
          </Box>
        )}

        {agency === 'moph' && (
          <Box sx={{ mb: 3 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {t("Primary Function")}: {data.primaryFunction}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {t("Source")}: {data.source}
              </Typography>
            </Alert>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {t("Service Areas")}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {data.serviceAreas.map((area, index) => (
                <Chip key={index} label={area} color="primary" variant="outlined" />
              ))}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Unit Types Table */}
      {data.unitTypes && (
        <Paper elevation={2} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
            {t("Aid by Measurement Units")}
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("Unit Type")}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">{t("Quantity")}</TableCell>
                  {data.unitTypes[0].weight !== undefined && (
                    <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">{t("Weight (Tons)")}</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.unitTypes.map((row, index) => (
                  <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}>
                    <TableCell sx={{ fontWeight: 600 }}>{row.type}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {row.quantity.toLocaleString()}
                    </TableCell>
                    {row.weight !== undefined && (
                      <TableCell align="right" sx={{ fontWeight: 600, color: 'secondary.main' }}>
                        {row.weight}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Top Donor Countries */}
      {data.topDonors && (
        <Paper elevation={2} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
            {t("Top Donor Countries")}
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'success.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("Donor")}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">{t("Units")} {agency === 'gec' ? t('Received') : ''}</TableCell>
                  {agency === 'gec' && (
                    <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">{t("Units Distributed")}</TableCell>
                  )}
                  {agency === 'hrc' && (
                    <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">{t("Distribution Rate")}</TableCell>
                  )}
                  {agency === 'south' && (
                    <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">{t("Weight (Tons)")}</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.topDonors.map((row, index) => (
                  <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {row.flag ? (
                          <Avatar src={row.flag} variant="square" alt={row.country} sx={{ width: 32, height: 32, mr: 2, img: { objectFit: "contain" } }} />
                        ) : (
                          <Avatar variant="square" sx={{ bgcolor: 'primary.main', width: 32, height: 32, mr: 2, img: { objectFit: "contain" } }}>
                            <Flag />
                          </Avatar>
                        )}
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {row.country}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: 'success.main' }}>
                      {typeof row.units === 'number' ? row.units.toLocaleString() : 
                       typeof row.received === 'number' ? row.received.toLocaleString() : row.units || row.received}
                    </TableCell>
                    {agency === 'gec' && (
                      <TableCell align="right" sx={{ fontWeight: 600, color: 'info.main' }}>
                        {typeof row.distributed === 'number' ? row.distributed.toLocaleString() : row.distributed}
                      </TableCell>
                    )}
                    {agency === 'hrc' && (
                      <TableCell align="right">
                        <Chip 
                          label={row.rate} 
                          color={row.anomaly ? 'error' : 'success'} 
                          size="small" 
                        />
                        {row.anomaly && (
                          <Typography variant="caption" sx={{ display: 'block', color: 'error.main' }}>
                            {t("*Data inconsistency")}
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    {agency === 'south' && (
                      <TableCell align="right" sx={{ fontWeight: 600, color: 'secondary.main' }}>
                        {row.weight}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Categories or Focus Areas */}
      {(data.categories || data.focusAreas) && (
        <Paper elevation={2} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
            {agency === 'gec' ? t('Primary Focus Areas') : t('Aid Categories')}
          </Typography>
          {data.categories && Array.isArray(data.categories) && typeof data.categories[0] === 'object' ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'info.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("Category")}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">{t("Units")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.categories.map((row, index) => (
                    <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}>
                      <TableCell sx={{ fontWeight: 600 }}>{row.category}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: 'info.main' }}>
                        {row.units.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : data.focusAreas ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'info.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("Focus Area")}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">{t("Percentage")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.focusAreas.map((row, index) => (
                    <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}>
                      <TableCell sx={{ fontWeight: 600 }}>{row.area}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: 'info.main' }}>
                        {row.percentage}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box>
              {data.categories.map((category, index) => (
                <Chip key={index} label={category} sx={{ mr: 1, mb: 1 }} color="primary" />
              ))}
            </Box>
          )}
        </Paper>
      )}

      {/* Geographic Issues */}
      {data.geographicIssue && (
        <Alert severity="warning" sx={{ mb: 4 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>{t("Geographic Distribution Issues")}</AlertTitle>
          <Typography variant="body1">
            {data.geographicIssue}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {t("Identified Locations")}: {t("Only 29% of distributions have clear geographic data")}
          </Typography>
        </Alert>
      )}

      {/* Special sections for specific agencies */}
      {agency === 'south' && data.villageDistribution && (
        <Paper elevation={2} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
            {t("Village Distribution Network")}
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'secondary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("Item")}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">{t("Quantity")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.villageDistribution.map((row, index) => (
                  <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}>
                    <TableCell sx={{ fontWeight: 600 }}>{row.item}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: 'secondary.main' }}>
                      {typeof row.quantity === 'number' ? row.quantity.toLocaleString() : row.quantity}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="body1" sx={{ mt: 2, fontStyle: 'italic' }}>
            {data.coverage}
          </Typography>
        </Paper>
      )}

      {agency === 'moph' && (
        <>
          {/* International Support */}
          <Paper elevation={2} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
              {t("International Support Sources")}
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'warning.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("Organization")}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("Support Type")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.internationalSupport.map((row, index) => (
                    <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}>
                      <TableCell sx={{ fontWeight: 600 }}>{row.organization}</TableCell>
                      <TableCell>{row.type}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Monthly Financial Distribution */}
          <Paper elevation={2} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
              {t("Monthly Financial Distribution")}
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'error.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("Month")}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">{t("Amount (Million USD)")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.monthlyFinancial.map((row, index) => (
                    <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}>
                      <TableCell sx={{ fontWeight: 600 }}>{row.month}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: 'error.main' }}>
                        ${row.amount}M
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Container>
  );
};

export default AgencyStats;
